/**
 * CustomerCard — relationship view for a single customer (keyed by email).
 *
 * Surfaces:
 *   • Header: name, email, outstanding $, paid 90d, master 🔕 toggle, statement button
 *   • All-time invoice list with per-row PDF link & status chip
 *   • Mini-AR stats (per-tone pay-rates)
 *   • Consolidated reminder log (last 50 reminders across all invoices)
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import {
  ArrowLeft, ArrowsCounterClockwise, Bell, BellSlash, FileText, FilePdf,
  EnvelopeSimple, CurrencyDollar, ChartLineUp, User, ListChecks,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  getCustomerCard, bulkSkipCustomer, sendCustomerStatement,
  customerStatementPdfUrl, invoicePdfUrl,
} from '../lib/api';
import ScheduleFollowupDialog from '../components/ScheduleFollowupDialog';

function formatCurrency(amount, ccy) {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: ccy || 'AUD' })
      .format(amount || 0);
  } catch (_) { return `${(amount || 0).toFixed(2)} ${ccy || 'AUD'}`; }
}
function pct(v) { return `${Math.round((v || 0) * 100)}%`; }
function relTime(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d`;
  return `${Math.floor(d / 30)}mo`;
}

const TONE_STYLE = {
  friendly: 'bg-violet-50 border-violet-300 text-violet-700',
  firm: 'bg-orange-50 border-orange-300 text-orange-700',
  final: 'bg-rose-100 border-rose-400 text-rose-800',
};

const STATUS_CHIP = {
  paid: 'bg-emerald-50 border-emerald-300 text-emerald-700',
  unpaid: 'bg-zinc-50 border-zinc-300 text-zinc-600',
};

export default function CustomerCard() {
  const { email: emailParam } = useParams();
  const email = decodeURIComponent(emailParam || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [skipDialog, setSkipDialog] = useState(false);
  const [sendingStmt, setSendingStmt] = useState(false);
  const [followupOpen, setFollowupOpen] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await getCustomerCard(email);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load customer');
      setData(null);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, [email]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBulkToggle = async () => {
    const next = !data.do_not_auto_remind;
    setSkipDialog(false);
    try {
      const r = await bulkSkipCustomer(email, next);
      toast.success(
        next
          ? `${data.name} excluded — ${r.data.invoices_modified} invoice${r.data.invoices_modified === 1 ? '' : 's'} & ${r.data.orders_modified} order${r.data.orders_modified === 1 ? '' : 's'} skipped`
          : `${data.name} re-included in auto-reminders`
      );
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update');
    }
  };

  const handleSendStatement = async () => {
    setSendingStmt(true);
    try {
      const r = await sendCustomerStatement(email, true);
      const tail = r.data.pay_url ? ' · pay link attached' : '';
      toast.success(`Statement sent to ${email} · ${r.data.outstanding_count} invoice${r.data.outstanding_count === 1 ? '' : 's'} · ${formatCurrency(r.data.grand_total, r.data.currency)} [${r.data.email_status}]${tail}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send statement');
    } finally { setSendingStmt(false); }
  };

  if (loading && !data) {
    return <Layout><div className="p-8 text-sm italic text-zinc-400" data-testid="customer-card-loading">Loading customer…</div></Layout>;
  }
  if (!data) {
    return (
      <Layout>
        <div className="p-8 space-y-4" data-testid="customer-card-empty">
          <Link to="/invoices/outstanding" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900"><ArrowLeft size={12} /> Back to Outstanding Invoices</Link>
          <div className="bg-white border border-zinc-200 p-8 text-center">
            <p className="text-sm text-zinc-500">No invoices found for <span className="font-mono">{email}</span>.</p>
          </div>
        </div>
      </Layout>
    );
  }

  const sent = data.ar_stats?.reminders_sent || {};
  const pr = data.ar_stats?.pay_rate_within_7d || {};
  const totalSent = (sent.friendly || 0) + (sent.firm || 0) + (sent.final || 0);
  const skipped = !!data.do_not_auto_remind;
  const outstandingInvoices = (data.invoices || []).filter((i) => i.payment_status !== 'paid' && i.status === 'issued');

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="customer-card-page">
        <div className="flex items-center gap-2">
          <Link to="/invoices/outstanding" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900" data-testid="customer-back">
            <ArrowLeft size={12} /> Back to Outstanding Invoices
          </Link>
        </div>

        {/* Header */}
        <div className="bg-white border-2 border-violet-300 p-5 grid lg:grid-cols-12 gap-4 items-center">
          <div className="lg:col-span-5 flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 bg-violet-100 border border-violet-300 flex items-center justify-center shrink-0">
              <User size={24} weight="bold" className="text-violet-700" />
            </div>
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-widest text-zinc-500">Customer</div>
              <h1 className="text-2xl font-bold tracking-tight truncate" data-testid="customer-name">{data.name}</h1>
              <div className="text-xs font-mono text-zinc-500 truncate" data-testid="customer-email">{data.email}</div>
            </div>
          </div>

          <div className="lg:col-span-2 border-l border-zinc-200 pl-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Outstanding</div>
            <div className="text-xl font-bold font-mono" data-testid="customer-outstanding">{formatCurrency(data.outstanding_total, data.currency)}</div>
          </div>

          <div className="lg:col-span-2 border-l border-zinc-200 pl-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Paid · last 90d</div>
            <div className="text-xl font-bold font-mono text-emerald-700" data-testid="customer-paid-90d">{formatCurrency(data.paid_90d, data.currency)}</div>
          </div>

          <div className="lg:col-span-3 flex flex-wrap gap-2 justify-end">
            <Button
              onClick={() => setFollowupOpen(true)}
              variant="outline"
              data-testid="customer-schedule-followup"
              title="Schedule an AR follow-up task"
            >
              <ListChecks size={14} weight="bold" className="mr-1" /> Follow-up
            </Button>
            <Button
              onClick={() => setSkipDialog(true)}
              variant={skipped ? 'default' : 'outline'}
              className={skipped ? 'bg-zinc-700 hover:bg-zinc-800' : ''}
              data-testid="customer-bulk-skip"
            >
              {skipped ? <BellSlash size={14} weight="bold" className="mr-1" /> : <Bell size={14} className="mr-1" />}
              {skipped ? 'Auto-reminders OFF' : 'Skip auto-reminders'}
            </Button>
            <Button
              onClick={handleSendStatement}
              disabled={sendingStmt || outstandingInvoices.length === 0}
              data-testid="customer-send-statement"
              className="bg-violet-600 hover:bg-violet-700"
              title={outstandingInvoices.length === 0 ? 'No outstanding invoices to summarise' : `Email a Statement of Account to ${data.email}`}
            >
              <EnvelopeSimple size={14} weight="bold" className="mr-1" />
              {sendingStmt ? 'Sending…' : 'Send statement'}
            </Button>
          </div>
        </div>

        {/* AR mini-stats */}
        <div className="bg-white border border-zinc-200 p-4 space-y-3" data-testid="customer-ar-stats">
          <div className="flex items-center gap-2">
            <ChartLineUp size={14} weight="bold" className="text-violet-600" />
            <h2 className="text-xs font-bold uppercase tracking-widest">Reminder effectiveness · all-time</h2>
            <span className="text-[10px] text-zinc-400 ml-2">Total sent: {totalSent}</span>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {['friendly', 'firm', 'final'].map((tone) => (
              <div key={tone} className={`px-3 py-2 border ${TONE_STYLE[tone]}`} data-testid={`customer-tone-${tone}`}>
                <div className="text-[10px] font-bold uppercase tracking-widest">{tone}</div>
                <div className="text-base font-bold font-mono">{sent[tone] || 0} sent</div>
                <div className="text-[11px]">Pay-rate ≤ 7d: <strong>{pct(pr[tone])}</strong></div>
              </div>
            ))}
          </div>
        </div>

        {/* Invoice list */}
        <div className="bg-white border border-zinc-200" data-testid="customer-invoice-list">
          <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
            <h2 className="text-xs font-bold uppercase tracking-widest flex items-center gap-2">
              <CurrencyDollar size={14} weight="bold" className="text-emerald-600" />
              Invoices ({data.invoices.length})
            </h2>
            <a
              href={customerStatementPdfUrl(email)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 border border-zinc-300 text-[11px] font-bold uppercase tracking-wider hover:bg-zinc-50"
              data-testid="customer-statement-pdf"
              title="Download Statement of Account PDF"
            >
              <FilePdf size={12} weight="bold" /> Statement PDF
            </a>
          </div>
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b">
              <tr>
                <th className="py-2 px-3 text-left">Invoice</th>
                <th className="py-2 px-3 text-left">Issued</th>
                <th className="py-2 px-3 text-left">Due</th>
                <th className="py-2 px-3 text-left">Status</th>
                <th className="py-2 px-3 text-right">Amount</th>
                <th className="py-2 px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`customer-invoice-row-${inv.id}`}>
                  <td className="py-2 px-3 font-mono font-bold">{inv.invoice_number}</td>
                  <td className="py-2 px-3 text-zinc-600">
                    {inv.issued_at ? new Date(inv.issued_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="py-2 px-3 text-zinc-600">
                    {inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '—'}
                  </td>
                  <td className="py-2 px-3">
                    <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase border ${STATUS_CHIP[inv.payment_status] || STATUS_CHIP.unpaid}`}>
                      {inv.payment_status}
                    </span>
                    {inv.payment_status !== 'paid' && inv.days_overdue > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold uppercase border bg-rose-50 border-rose-300 text-rose-700">
                        {inv.days_overdue}d
                      </span>
                    )}
                    {inv.reminders_count > 0 && (
                      <span className={`ml-1 px-1.5 py-0.5 text-[10px] font-bold uppercase border ${TONE_STYLE[inv.last_reminder_tone] || TONE_STYLE.friendly}`}>
                        ✉ {inv.reminders_count}×
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-right font-mono font-bold">{formatCurrency(inv.grand_total, inv.currency)}</td>
                  <td className="py-2 px-3 text-right">
                    <a href={invoicePdfUrl(inv.id)} target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-1 px-2 py-1 border border-zinc-300 text-[10px] font-bold uppercase hover:bg-zinc-50"
                       data-testid={`customer-row-pdf-${inv.id}`}>
                      <FileText size={11} /> PDF
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Reminder log */}
        <div className="bg-white border border-zinc-200" data-testid="customer-reminder-log">
          <div className="px-4 py-3 border-b border-zinc-200">
            <h2 className="text-xs font-bold uppercase tracking-widest flex items-center gap-2">
              <EnvelopeSimple size={14} weight="bold" className="text-violet-600" />
              Reminder timeline ({data.reminder_log?.length || 0})
            </h2>
          </div>
          {(!data.reminder_log || data.reminder_log.length === 0) ? (
            <div className="p-8 text-center text-xs text-zinc-400 italic">No reminders sent yet.</div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {data.reminder_log.map((r, i) => (
                <li key={i} className="px-4 py-2 flex items-start gap-3" data-testid={`customer-reminder-${i}`}>
                  <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase border ${TONE_STYLE[r.tone] || TONE_STYLE.friendly} shrink-0`}>
                    {r.tone}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs">
                      <span className="font-bold">{r.invoice_number}</span>
                      <span className="text-zinc-400"> · {r.subject || '—'}</span>
                    </div>
                    <div className="text-[10px] text-zinc-400 flex flex-wrap gap-2">
                      <span>{new Date(r.sent_at).toLocaleString()}</span>
                      <span>· {relTime(r.sent_at)} ago</span>
                      <span>· {r.auto ? 'auto' : 'manual'}</span>
                      <span>· {r.email_status || 'sent'}</span>
                      {r.pay_url && <span className="text-violet-600">· pay link</span>}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <Button onClick={refresh} variant="outline" size="sm" data-testid="customer-refresh">
          <ArrowsCounterClockwise size={12} className="mr-1" /> Refresh
        </Button>
      </div>

      <AlertDialog open={skipDialog} onOpenChange={setSkipDialog}>
        <AlertDialogContent data-testid="customer-bulk-skip-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {skipped ? 'Re-include' : 'Exclude'} {data.name} from auto-reminders?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will flip <code>do_not_auto_remind</code> on all <strong>{data.invoices.length}</strong> of this customer's invoices and their orders in one click.
              Manual reminders are unaffected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="customer-bulk-skip-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkToggle}
              className={skipped ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-zinc-700 hover:bg-zinc-800'}
              data-testid="customer-bulk-skip-confirm"
            >
              {skipped ? 'Yes, re-include' : 'Yes, exclude all'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ScheduleFollowupDialog
        open={followupOpen}
        onOpenChange={setFollowupOpen}
        customerEmail={data.email}
        customerName={data.name}
        invoices={(data.invoices || []).filter((i) => i.payment_status !== 'paid' && i.grand_total > 0)}
      />
    </Layout>
  );
}
